import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";
import { getTrades, updateTradeOutcomeDB, deleteTradeDB, regradeTradeDB } from "@/lib/supabaseServices";
import GradingCard from "@/components/GradingCard";
import {
  TrendingUp,
  TrendingDown,
  Trash2,
  ChevronDown,
  Check,
  X,
  Clock,
  Loader2,
  Filter,
  RefreshCw,
  Download,
  Tag,
} from "lucide-react";

const gradeColor = (g) => {
  if (g === "A+" || g === "A") return "#00FF41";
  if (g === "B") return "#FFD700";
  if (g === "C") return "#FF7A00";
  return "#FF003C";
};

const SESSIONS = ["London", "New York", "Asian"];
const GRADES = ["A+", "A", "B", "C", "D"];

const selectCls =
  "bg-[#050505] border border-[#1A1A24] px-3 py-2 text-xs text-[#E2E8F0] focus:outline-none focus:border-[#00F0FF]";

export default function TradeHistory({ refreshKey, onChange }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [regrading, setRegrading] = useState(null);
  const [filters, setFilters] = useState({ session: "", direction: "", grade: "", outcome: "", tag: "" });

  const load = () => {
    setLoading(true);
    getTrades(filters)
      .then((data) => setTrades(data))
      .catch(() => toast.error("Failed to load trades"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [refreshKey, filters]);

  const setOutcome = async (id, outcome) => {
    try {
      await updateTradeOutcomeDB(id, outcome);
      setTrades((prev) => prev.map((t) => (t.id === id ? { ...t, outcome } : t)));
      toast.success(`Marked ${outcome}`);
      onChange?.();
    } catch {
      toast.error("Failed to update outcome");
    }
  };

  const remove = async (id) => {
    try {
      await deleteTradeDB(id);
      setTrades((prev) => prev.filter((t) => t.id !== id));
      toast.success("Trade deleted");
      onChange?.();
    } catch {
      toast.error("Failed to delete trade");
    }
  };

  const regrade = async (id) => {
    setRegrading(id);
    try {
      const updated = await regradeTradeDB(id);
      setTrades((prev) => prev.map((t) => (t.id === id ? updated : t)));
      toast.success(`Re-graded: ${updated.grading?.icc_score ?? "?"}`);
      onChange?.();
    } catch {
      toast.error("Re-grade failed, try again");
    } finally {
      setRegrading(null);
    }
  };

  // ─── CSV Export ─────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (trades.length === 0) {
      toast.error("No trades to export");
      return;
    }
    const headers = ["Date", "Session", "Instrument", "Direction", "Grade", "Outcome", "R:R", "Strategy", "Tags", "Lesson"];
    const rows = trades.map((t) => [
      t.date,
      t.session,
      t.instrument,
      t.direction,
      t.grading?.icc_score || "N/A",
      t.outcome,
      t.rr_ratio || "N/A",
      `"${(t.custom_strategy || "").replace(/"/g, '""')}"`,
      `"${(t.tags || []).join(", ")}"`,
      `"${(t.grading?.lesson || "").replace(/"/g, '""')}"`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `icc_journal_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV exported successfully");
  };

  return (
    <div className="space-y-6" data-testid="trade-history">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-white leading-none">
            TRADE <span className="text-[#00F0FF] text-glow-cyan">LOG</span>
          </h1>
          <p className="text-[#848D97] text-sm mt-2">{trades.length} entries archived.</p>
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 px-4 py-2 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 text-xs uppercase tracking-wider transition-colors"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 border border-[#1A1A24] bg-[#0C0C0E] p-3">
        <Filter className="w-4 h-4 text-[#00F0FF]" />
        <select
          value={filters.session}
          onChange={(e) => setFilters({ ...filters, session: e.target.value })}
          className={selectCls}
          data-testid="filter-session"
        >
          <option value="">All Sessions</option>
          {SESSIONS.map((s) => (
            <option key={s} value={s} className="bg-[#0C0C0E]">
              {s}
            </option>
          ))}
        </select>
        <select
          value={filters.direction}
          onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
          className={selectCls}
          data-testid="filter-direction"
        >
          <option value="">All Directions</option>
          <option value="Long">Long</option>
          <option value="Short">Short</option>
        </select>
        <select
          value={filters.grade}
          onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
          className={selectCls}
          data-testid="filter-grade"
        >
          <option value="">All Grades</option>
          {GRADES.map((g) => (
            <option key={g} value={g} className="bg-[#0C0C0E]">
              {g}
            </option>
          ))}
        </select>
        <select
          value={filters.outcome}
          onChange={(e) => setFilters({ ...filters, outcome: e.target.value })}
          className={selectCls}
          data-testid="filter-outcome"
        >
          <option value="">All Outcomes</option>
          <option value="win">Win</option>
          <option value="loss">Loss</option>
          <option value="pending">Pending</option>
        </select>
        <input
          type="text"
          placeholder="Filter by Tag..."
          value={filters.tag}
          onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
          className={`${selectCls} w-36`}
        />
        {(filters.session || filters.direction || filters.grade || filters.outcome || filters.tag) && (
          <button
            onClick={() => setFilters({ session: "", direction: "", grade: "", outcome: "", tag: "" })}
            className="text-xs text-[#FF003C] px-2"
            data-testid="clear-filters"
          >
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#00F0FF]">
          <Loader2 className="w-7 h-7 animate-spin" />
        </div>
      ) : trades.length === 0 ? (
        <div className="border border-dashed border-[#1A1A24] py-24 text-center text-[#848D97]">
          No trades match. Log a setup or adjust filters.
        </div>
      ) : (
        <div className="space-y-3">
          {trades.map((t, i) => {
            const g = t.grading?.icc_score || "?";
            const gc = gradeColor(g);
            const isOpen = expanded === t.id;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.4) }}
                className="border border-[#1A1A24] bg-[#0C0C0E]"
                data-testid="trade-row"
              >
                <div className="flex items-center gap-4 p-4">
                  <div
                    className="font-display font-black text-2xl w-12 text-center flex-shrink-0"
                    style={{ color: gc, textShadow: `0 0 12px ${gc}66` }}
                  >
                    {g}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-bold text-sm">{t.instrument}</span>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border"
                        style={{
                          color: t.direction === "Long" ? "#00FF41" : "#FF003C",
                          borderColor: t.direction === "Long" ? "#00FF41" : "#FF003C",
                        }}
                      >
                        {t.direction === "Long" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {t.direction}
                      </span>
                      <span className="text-[11px] text-[#848D97]">{t.session}</span>
                      <span className="text-[11px] text-[#848D97]">· {t.date}</span>
                      {t.rr_ratio && <span className="text-[11px] text-[#FFD700]">· 1:{t.rr_ratio}</span>}
                    </div>

                    {/* Tag badges */}
                    {t.tags && t.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {t.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.2 border border-[#00F0FF]/30 text-[#00F0FF] bg-[#00F0FF]/5"
                          >
                            <Tag className="w-2.5 h-2.5" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="text-[11px] text-[#848D97] mt-0.5 truncate">
                      {t.grading?.lesson || (
                        <span className="text-[#FFD700]">{t.grading_error || "Not graded yet"}</span>
                      )}
                    </div>
                  </div>

                  {!t.grading && (
                    <button
                      onClick={() => regrade(t.id)}
                      disabled={regrading === t.id}
                      title="Re-grade"
                      data-testid={`regrade-${t.id}`}
                      className="flex items-center gap-1 px-2 py-1.5 border border-[#FFD700] text-[#FFD700] text-[11px] hover:bg-[#FFD700]/10 flex-shrink-0 disabled:opacity-50"
                    >
                      {regrading === t.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                      )}
                      Re-grade
                    </button>
                  )}

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setOutcome(t.id, "win")}
                      title="Win"
                      data-testid={`mark-win-${t.id}`}
                      className={`p-1.5 border transition-colors ${
                        t.outcome === "win"
                          ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/10"
                          : "border-[#1A1A24] text-[#848D97] hover:text-[#00FF41]"
                      }`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setOutcome(t.id, "loss")}
                      title="Loss"
                      data-testid={`mark-loss-${t.id}`}
                      className={`p-1.5 border transition-colors ${
                        t.outcome === "loss"
                          ? "border-[#FF003C] text-[#FF003C] bg-[#FF003C]/10"
                          : "border-[#1A1A24] text-[#848D97] hover:text-[#FF003C]"
                      }`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    {t.outcome === "pending" && <Clock className="w-3.5 h-3.5 text-[#848D97]" />}
                  </div>

                  <button
                    onClick={() => setExpanded(isOpen ? null : t.id)}
                    data-testid={`expand-${t.id}`}
                    className="p-1.5 text-[#848D97] hover:text-[#00F0FF]"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <button
                    onClick={() => remove(t.id)}
                    data-testid={`delete-${t.id}`}
                    className="p-1.5 text-[#848D97] hover:text-[#FF003C]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden border-t border-[#1A1A24]"
                    >
                      <div className="p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                          <div className="border border-[#1A1A24] p-3">
                            <div className="text-[#00F0FF] uppercase tracking-wider mb-1">Indication</div>
                            <div className="text-[#E2E8F0]">{t.indication}</div>
                          </div>
                          <div className="border border-[#1A1A24] p-3">
                            <div className="text-[#FFD700] uppercase tracking-wider mb-1">Correction</div>
                            <div className="text-[#E2E8F0]">{t.correction}</div>
                          </div>
                          <div className="border border-[#1A1A24] p-3">
                            <div className="text-[#00FF41] uppercase tracking-wider mb-1">Continuation</div>
                            <div className="text-[#E2E8F0]">{t.continuation}</div>
                          </div>
                        </div>

                        {t.custom_strategy && (
                          <div className="text-xs text-[#848D97]">
                            <span className="text-[#00F0FF]">STRATEGY:</span> {t.custom_strategy}
                          </div>
                        )}

                        {t.screenshot_path && (
                          <img
                            src={fileUrl(t.screenshot_path)}
                            alt="chart"
                            className="w-full max-w-lg border border-[#1A1A24]"
                          />
                        )}

                        {t.grading ? (
                          <GradingCard grading={t.grading} />
                        ) : (
                          <div className="border border-[#FFD700]/40 bg-[#FFD700]/5 p-5 flex items-center justify-between gap-4">
                            <div className="text-sm text-[#E2E8F0]">
                              {t.grading_error || "This trade hasn't been graded yet."}
                            </div>
                            <button
                              onClick={() => regrade(t.id)}
                              disabled={regrading === t.id}
                              data-testid={`regrade-detail-${t.id}`}
                              className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-black text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                            >
                              {regrading === t.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                              Re-grade now
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
